/* eslint-disable max-len */
/* eslint-disable no-bitwise */
import { crc16 } from 'ngx-modbus';

import {
  SgpCommand,
  SgpCommandDistributeFirmware,
  SgpCommandGetFirmwareVersion,
  SgpCommandHandshake,
  SgpCommandQueryFirmwareBitMap,
  SgpCommandStartFirmwareDownload,
  SgpCommandValidateFirmware,
} from './command.model';
import { SgpCommandCode, SgpFlagMode, SgpMasterDeviceType } from './general.model';
import { ControlWordParams } from './response.model';
import { SgpSupport } from './sgp-support.model';

export class SgpCommandBuilder {
  static ProtocolVersion = 0x01;
  /**
   * The maximum queue size
   * This data was taken from the serial connection with the device.
   */
  static SizeOfReceivingBuffer = 0x0400;
  static DeviceType = SgpMasterDeviceType.UpperComputer;

  /**
   * Data frame structure:
   *  - For one-to-one environment
   *    Flag(2 Bytes) | FCS(2 Byte) | Command(2 Byte) | Length(2 Byte) | Info(Length - N Bytes)
   *    Flag field  | Validation field | Command field | Length field (N) | Information field
   *
   *   - For one-to-many environment
   *    Flag(2 Bytes) | FCS(2 Byte) | Control(2 Byte) | Command(2 Byte) | Length(2 Byte) | Addr(x Bytes) | Info(Length - N Bytes)
   *    Flag field  | Validation field | Control word | Command field | Length field (N + x) | Address field | Information field
   *   Address field: xBytes is confirmed by the control word
   */
  static ComposeCommand(
    flag: SgpFlagMode,
    fcs: Uint8Array,
    command: Uint8Array,
    length: Uint8Array,
    info: Uint8Array,
    addressField?: Uint8Array,
    controlWord?: Uint8Array,
  ) {
    if (flag === SgpFlagMode.Normal) {
      return new Uint8Array([
        ...SgpSupport.FlagCode(flag),
        ...fcs,
        ...command,
        ...length,
        ...info,
      ]);
    }

    if (!controlWord || !addressField) {
      throw new Error(
        'controlWord and addressField are required in advanced mode',
      );
    }

    return new Uint8Array([
      ...SgpSupport.FlagCode(flag),
      ...fcs,
      ...controlWord,
      ...command,
      ...length,
      ...addressField,
      ...info,
    ]);
  }

  /**
   *
   * @param flag define the flag mode (Normal or Advanced)
   * @param sgpCommand define the command type (Handshake, ...) & params
   * @param addressField define the address field (optional), only used in advanced mode. 0 represents broadcast
   * @param controlWordParams define the control word params (optional), only used in advanced mode
   * @returns the command frame
   */
  static BuildCommand(flag: SgpFlagMode, sgpCommand: SgpCommand, addressField?: number, controlWordParams?: ControlWordParams) {

    const command = SgpSupport.CommandField(sgpCommand.type, { masterToSlave: true });
    let info: Uint8Array;
    let controlWord: Uint8Array | undefined;

    switch (sgpCommand.type) {
      case SgpCommandCode.Handshake:
        info = SgpCommandBuilder.BuildHandshakeInformationField(sgpCommand);
        break;

      case SgpCommandCode.GetFirmwareVersion:
        info = SgpCommandBuilder.BuildGetFirmwareVersionInformationField(sgpCommand);
        break;

      case SgpCommandCode.StartFirmwareDownload:
        info = SgpCommandBuilder.BuildSgpCommandStartFirmwareDownloadInformationField(sgpCommand);
        break;

      case SgpCommandCode.DistributeFirmware:
        info = SgpCommandBuilder.BuildSgpCommandDistributeFirmwareInformationField(sgpCommand);
        break;

      case SgpCommandCode.ValidateFirmware:
        info = SgpCommandBuilder.BuildSgpCommandValidateFirmwareInformationField(sgpCommand);
        break;

      case SgpCommandCode.QueryFirmwareBitMap:
        info = SgpCommandBuilder.BuildSgpCommandQueryFirmwareBitMapInformationField(sgpCommand);
        break;

      default:
        throw new Error('Command type is not supported');
    }

    const lengthIndicationField = SgpSupport.LengthIndicationField(info, addressField);

    if (flag === SgpFlagMode.Advanced && controlWordParams && addressField !== undefined) {
      /**
       * addressLength is a params between 0 ~ 7 (bytes) and is used to determine the length of the address field
       */
      const addressLength = Math.ceil(Math.log2(addressField + 1) / 8);

      controlWord = SgpSupport.ControlWord(
        controlWordParams.isEncrypted,
        controlWordParams.needResponse,
        controlWordParams.frameNumber,
        addressLength,
        controlWordParams.frameFormatVersion,
      );
    }

    /**
     * Validation field (FCS) = ValidationField
     * (control word) + command field + length indication field + + (address field) + information field.
     */
    const { highByte, lowByte } = crc16(new Uint8Array([
      ...(controlWord !== undefined ? controlWord : []),
      ...command,
      ...lengthIndicationField,
      ...(addressField !== undefined ? new Uint8Array([addressField]) : []),
      ...info,
    ]));

    return SgpCommandBuilder.ComposeCommand(
      flag,
      new Uint8Array([lowByte, highByte]),
      command,
      lengthIndicationField,
      info,
      addressField !== undefined ? new Uint8Array([addressField]) : undefined,
      controlWord,
    );
  }

  /**
   * Format the information field
   * Protocol version (1 Byte) | Size of single buffer (2 Bytes) | Device type (1 Byte)
   *  -  Protocol version, BCD code, default V0.1
   *  -  Size of single buffer, Return the biggest size of a single buffer
   *  -  Device type, 0 PC, 1 upper computer
   *
   * @Note
   *   Device is cell phone -  upper computer
   */
  static BuildHandshakeInformationField(sgpCommand: SgpCommandHandshake): Uint8Array {
    const { protocolVersion, maxSizeOfSingleBuffer, masterDeviceType } = sgpCommand;
    const informationField = new Uint8Array(4);
    informationField[0] = protocolVersion;
    informationField[1] = maxSizeOfSingleBuffer >> 8;
    informationField[2] = maxSizeOfSingleBuffer & 0xff;
    informationField[3] = masterDeviceType;
    return informationField;
  }

  /**
   * Format the information field
   * Supported device type (1 Byte) - SgpSupportedDeviceType
   */
  static BuildGetFirmwareVersionInformationField(sgpCommand: SgpCommandGetFirmwareVersion): Uint8Array {
    const { slaveDeviceType } = sgpCommand;
    const informationField = new Uint8Array(1);
    informationField[0] = slaveDeviceType;
    return informationField;
  }

  /**
   * Format the information field
   * Firmware name (20 Bytes) | Firmware version (20 Bytes) | Firmware size (4 Bytes) | Firmware CRC32 (4 Bytes) | Firmware effect (1 Byte)
   */
  static BuildSgpCommandStartFirmwareDownloadInformationField(sgpCommand: SgpCommandStartFirmwareDownload): Uint8Array {
    const { firmwareName, firmwareVersion, firmwareSize, firmwareCRC32, firmwareEffect } = sgpCommand;
    const informationField = new Uint8Array(49);
    informationField.set(new TextEncoder().encode(firmwareName), 0);
    informationField.set(new TextEncoder().encode(firmwareVersion), 20);
    informationField.set(firmwareSize, 40);
    informationField.set(firmwareCRC32, 44);
    informationField.set(firmwareEffect, 48);
    return informationField;
  }

  /**
   * Format the information field
   * File CRC32 (4 Bytes) | Offset address (4 Bytes) | Data length (4 Bytes) | Data (n Bytes)
   */
  static BuildSgpCommandDistributeFirmwareInformationField(sgpCommand: SgpCommandDistributeFirmware): Uint8Array {
    const { fileCRC32, offsetAddress, dataLength, data } = sgpCommand;
    const contentLength = dataLength[0] | dataLength[1] << 8 | dataLength[2] << 16 | dataLength[3] << 24;
    const informationField = new Uint8Array(12 + contentLength);
    informationField.set(fileCRC32, 0);
    informationField.set(offsetAddress, 4);
    informationField.set(dataLength, 8);
    informationField.set(data, 12);
    return informationField;
  }

  /**
   * Format the information field
   * File CRC32 (4 Bytes) | Upgrade time (6 Bytes)
   * -  Upgrade time, BCD code, YYMMDDhhmmss
   * -  Example: 2020/12/31 23:59:59, 20 12 31 23 59 59
   *
   * @Note If the upgrade time is 0, upgrade immediately once the firmware is downloaded
   */
  static BuildSgpCommandValidateFirmwareInformationField(sgpCommand: SgpCommandValidateFirmware): Uint8Array {
    const { fileCRC32, upgradeTime } = sgpCommand;
    const informationField = new Uint8Array(10);
    informationField.set(fileCRC32, 0);
    informationField.set(upgradeTime, 4);
    return informationField;
  }

  /**
   * Format the information field
   * File CRC32 (4 Bytes) | Query offset of bit map (2 Bytes) | Query number of bit map (2 Bytes)
   */
  static BuildSgpCommandQueryFirmwareBitMapInformationField(sgpCommand: SgpCommandQueryFirmwareBitMap): Uint8Array {
    const { fileCRC32, queryOffsetOfBitMap, queryNumberOfBitMap } = sgpCommand;
    const informationField = new Uint8Array(8);
    informationField.set(fileCRC32, 0);
    informationField.set(queryOffsetOfBitMap, 4);
    informationField.set(queryNumberOfBitMap, 6);
    return informationField;
  }
}
